import { collection, doc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { INVOICE_STATUS_OPTIONS } from '../../../utils/invoiceStatuses';
import type { Company, FamilyMember } from './types';
import type { Document } from '../../../components/DocumentTile';

export async function getCompanyById(companyId: string, t: (k: string) => string): Promise<Company | null> {
  const companyDoc = await getDoc(doc(db, 'companies', companyId));
  if (!companyDoc.exists()) return null;
  const companyData = companyDoc.data();
  const company: Company = {
    id: companyDoc.id,
    name: companyData?.name || t('company_page_unknown_company'),
    ...companyData,
  } as Company;
  return company;
}

export async function deleteCompany(companyId: string): Promise<void> {
  try {
    const companyRef = doc(db, 'companies', companyId);
    await deleteDoc(companyRef);
  } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ deleteCompany: Error deleting company:', error);
      }
    throw error;
  }
}

export async function getCompanyUsers(companyId: string, t: (k: string) => string): Promise<FamilyMember[]> {
  const usersRef = collection(db, 'users');
  const qUsers = query(usersRef, where('company', '==', companyId));
  const querySnapshot = await getDocs(qUsers);
  const users: FamilyMember[] = querySnapshot.docs.map(d => {
    const data = d.data() as any;
    return {
      id: d.id,
      name: data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : data.name || t('global_unknown_user'),
      email: data.email || '',
      status: data.status || 'active',
      avatar: data.avatar || '',
      role: data.role || '',
      type: data.type || [1],
      createdAt: data.createdAt?.toDate?.()?.getTime() || Date.now(),
      project: data.project || {},
      company: data.company || companyId, // Include company field, default to companyId if not set
    } as FamilyMember;
  });
  return users;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
}

export async function getCompanyDocuments(companyId: string, t: (k: string) => string): Promise<Document[]> {
  // First get all users of this company
  const usersRef = collection(db, 'users');
  const usersQuery = query(usersRef, where('company', '==', companyId));
  const usersSnapshot = await getDocs(usersQuery);
  const userIds = usersSnapshot.docs.map(d => d.id);
  if (userIds.length === 0) return [];

  const documentsRef = collection(db, 'invoices');
  const idChunks = chunkArray<string>(userIds, 30);
  const snapshots = await Promise.all(
    idChunks.map(ids => getDocs(query(documentsRef, where('userId', 'in', ids))))
  );

  const documents: Document[] = [];
  snapshots.forEach(snapshot => {
    snapshot.docs.forEach(d => {
      const data = d.data() as any;
      documents.push({
        id: d.id,
        documentId: data.documentId || '',
        title: data.name || data.title || t('global_untitled'),
        sender: data.sender || t('global_unknown'),
        createdAt: data.createdAt?.toDate?.() || new Date(),
        detectedDocumentCategory: data.detectedDocumentCategory || t('global_unknown'),
        status: data.status || 'submitted',
        taskCount: data.taskCount || 0,
        doneTaskCount: data.doneTaskCount || 0,
        currency: data.currency,
        dueDate: data.dueDate,
        invoiceDate: data.invoiceDate,
        invoiceNumber: data.invoiceNumber,
        paymentTerms: data.paymentTerms,
        receiver: data.receiver,
        senderWebsiteUrl: data.senderWebsiteUrl,
        summary: data.summary,
        subtotal: data.subtotal,
        taxAmount: data.taxAmount,
        totalAmount: data.totalAmount,
        userId: data.userId,
        content: data.content,
      } as Document);
    });
  });
  return documents;
}

export function buildPagination(totalItems: number, currentPage: number, itemsPerPage: number) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = Math.max(1, safePage - 2);
  const end = Math.min(totalPages, start + 4);
  const adjStart = Math.max(1, end - 4);
  const visiblePages = Array.from({ length: end - adjStart + 1 }, (_, i) => adjStart + i);
  return {
    currentPage: safePage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage: safePage < totalPages,
    hasPreviousPage: safePage > 1,
    visiblePages,
  };
}

export function paginate<T>(items: T[], page: number, itemsPerPage: number) {
  const startIdx = (page - 1) * itemsPerPage;
  return items.slice(startIdx, startIdx + itemsPerPage);
}

export function filterDocuments(
  documents: Document[],
  params: { search: string; status: string; category: string }
): Document[] {
  const { search, status, category } = params;
  const q = (search || '').toLowerCase();
  return documents.filter(d => {
    const matchesSearch = !q ||
      d.title?.toLowerCase().includes(q) ||
      d.sender?.toLowerCase().includes(q) ||
      d.detectedDocumentCategory?.toLowerCase().includes(q);
    const matchesStatus = !status || d.status === status;
    const matchesCategory = !category || d.detectedDocumentCategory === category;
    return matchesSearch && matchesStatus && matchesCategory;
  });
}

export function filterUsers(
  users: FamilyMember[],
  params: { search: string; status: string; role: string }
): FamilyMember[] {
  const { search, status, role } = params;
  const q = (search || '').toLowerCase();
  return users.filter(u => {
    const matchesSearch = !q ||
      u.name?.toLowerCase().includes(q) ||
      (u.email && u.email.toLowerCase().includes(q));
    const matchesStatus = !status || u.status === (status as any);
    const matchesRole = !role || (u as any).role === role;
    return matchesSearch && matchesStatus && matchesRole;
  });
}

export function buildInvoiceStatusOptions(t: (k: string) => string) {
  return INVOICE_STATUS_OPTIONS.map(option => ({
    value: option.value,
    label: t(`company_page_filter_${option.value}`),
  }));
}

export function buildCategoryOptions(t: (k: string) => string) {
  return [
    { value: 'Medical Invoice', label: t('company_page_category_medical_invoice') },
    { value: 'Lab Report', label: t('company_page_category_lab_report') },
    { value: 'Pharmacy Receipt', label: t('company_page_category_pharmacy_receipt') },
    { value: 'Dental Invoice', label: t('company_page_category_dental_invoice') },
    { value: 'Therapy Invoice', label: t('company_page_category_therapy_invoice') },
  ];
}

export function buildUserStatusOptions(t: (k: string) => string) {
  return [
    { value: 'active', label: t('company_page_filter_active') },
    { value: 'invited', label: t('company_page_filter_invited') },
    { value: 'blocked', label: t('company_page_filter_blocked') },
    { value: 'archive', label: t('company_page_filter_archive') },
  ];
}

export function buildUserRoleOptions(t: (k: string) => string) {
  return [
    { value: 'client_admin', label: t('user_role_client_admin') },
    { value: 'family_member', label: t('user_role_employee') },
  ];
}

export function extractSponsorshipBadge(companyData: any) {
  const sponsorshipSettings = companyData?.settings?.sponsorshipBadge;
  if (!sponsorshipSettings) return null as null | { enabled: boolean; text: string; logoUrl: string };
  return {
    enabled: sponsorshipSettings.enabled || false,
    text: sponsorshipSettings.text || '',
    logoUrl: sponsorshipSettings.logoUrl || '',
  };
}


